import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material/Select";
import dayjs, { Dayjs } from "dayjs";

import {
  getTaskById,
  updateTask,
  createTask,
  deleteTask,
} from "../services/taskService";
import { getSubtasks } from "../services/subtaskService";
import { getNotes, createNote } from "../services/noteService";

import { Task, Attachment, User, Subtask } from "../types";
import { ItemWithSource } from "../components/Cards/ItemCard";
import {
  TaskStatusKey,
  taskStatusColors,
  MeetingStatusKey,
} from "../utils/statusColors";
import { DEFAULT_CONFIG } from "../utils/config";
import {
  buildDraftKey,
  clearDraft,
  loadDraft,
  saveDraft,
} from "../utils/draftStorage";

import { DEVICE_TZ, toServerUtcIso, normalizeIsoUtc } from "../utils/time";

type StatusKey = "all" | TaskStatusKey | MeetingStatusKey;

interface LocalAttachment {
  attachment: Attachment;
  file?: File;
}

export const useTaskDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const defaultUserName = user?.name || t("tasks.currentUser");

  const [task, setTask] = useState<Task | null>(null);
  const [isEditing, setIsEditing] = useState(id === "new");
  const [form, setForm] = useState<Partial<Task>>({
    status: "Not Started" as TaskStatusKey,
    assignedUserId: user?.id || "fallback-user-id",
    assignedUserName: defaultUserName,
    name: "",
    description: "",
    dateStart: "",
    dateEnd: "",
    attachments: [],
  });
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [pendingSubtasks, setPendingSubtasks] = useState<Partial<Subtask>[]>(
    []
  );
  const [notes, setNotes] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [draftPromptOpen, setDraftPromptOpen] = useState(false);
  const [draftToRestore, setDraftToRestore] =
    useState<Partial<Task> | null>(null);
  const draftPromptShownRef = useRef(false);

  const queryParams = new URLSearchParams(location.search);
  const taskParentId = queryParams.get("parentId") ?? undefined;
  const draftKey = buildDraftKey("task", taskParentId || "root");

  const statusTranslations: Record<TaskStatusKey, string> = {
    "Not Started": t("statuses.task.not_started"),
    Started: t("statuses.task.started"),
    Completed: t("statuses.task.completed"),
    Deferred: t("statuses.task.deferred"),
    Сanceled: t("statuses.task.canceled"),
  };

  const loadAll = useCallback(async () => {
    if (!id || id === "new") {
      const pendingAttachments: Attachment[] = JSON.parse(
        localStorage.getItem("pendingAttachments") || "[]"
      );
      setAttachments(
        pendingAttachments.map((a: Attachment) => ({ attachment: a }))
      );
      if (taskParentId) {
        setForm((prev) => ({ ...prev, taskParentId }));
      }
      return;
    }

    setIsLoading(true);
    try {
      const tsk = await getTaskById(id);
      setTask(tsk);

      setForm({
        status: tsk.status as TaskStatusKey,
        assignedUserId: tsk.assignedUserId,
        assignedUserName: tsk.assignedUserName || defaultUserName,
        dateStart: tsk.dateStart ? tsk.dateStart : "",
        dateEnd: tsk.dateEnd ? tsk.dateEnd : "",
        description: tsk.description,
        resolution: tsk.cSolution ?? undefined,
        name: tsk.name,
        taskParentId: tsk.taskParentId,
        dateCompleted: tsk.dateCompleted ?? undefined,
      });

      const st = await getSubtasks(id);
      setSubtasks(st.list);
      setNotes(await getNotes(id));

      const attachmentsFromTask =
        tsk.attachmentsIds?.map((aid) => ({
          attachment: {
            id: aid,
            name: tsk.attachmentsNames?.[aid] || "",
            url: `/Attachment/file/${aid}`,
            type: tsk.attachmentsTypes?.[aid] || "",
            parentType: "Task",
            role: "Attachment",
          },
        })) || [];
      setAttachments(attachmentsFromTask);
    } finally {
      setIsLoading(false);
    }
  }, [id, defaultUserName, taskParentId]);

  const initNewForm = useCallback(() => {
    setForm({
      status: "Not Started" as TaskStatusKey,
      assignedUserId: user?.id || "fallback-user-id",
      assignedUserName: defaultUserName,
      name: "",
      description: "",
      dateStart: "",
      dateEnd: "",
      attachments: [],
      taskParentId,
    });
    setIsEditing(true);
    setPendingSubtasks([]);
    setNotes([]);
  }, [defaultUserName, taskParentId, user?.id]);

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const { getCachedUsers } = await import("../storage/preloadData");
        const usersList = await getCachedUsers();
        setUsers(usersList);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();

    if (id === "new") {
      initNewForm();
      loadAll();
    } else {
      setIsEditing(false);
      loadAll();
    }
  }, [id, defaultUserName, loadAll, user.id, taskParentId, initNewForm]);

  useEffect(() => {
    draftPromptShownRef.current = false;
    setDraftPromptOpen(false);
    setDraftToRestore(null);
  }, [id, taskParentId]);

  useEffect(() => {
    if (id !== "new") return;
    if (draftPromptShownRef.current) return;
    const run = async () => {
      const draft = await loadDraft<Partial<Task>>(draftKey);
      if (!draft?.data) return;

      const hasContent =
        !!draft.data.name ||
        !!draft.data.description ||
        !!draft.data.dateStart ||
        !!draft.data.dateEnd ||
        !!draft.data.resolution ||
        (draft.data.status && draft.data.status !== "Not Started");

      if (!hasContent) {
        await clearDraft(draftKey);
        return;
      }

      setDraftToRestore(draft.data);
      setDraftPromptOpen(true);
      draftPromptShownRef.current = true;
    };
    void run();
  }, [id, draftKey]);

  useEffect(() => {
    if (id !== "new") return;
    if (draftPromptOpen) return;
    const shouldSave =
      !!form.name ||
      !!form.description ||
      !!form.dateStart ||
      !!form.dateEnd ||
      !!form.resolution ||
      (form.status && form.status !== "Not Started");

    if (!shouldSave) {
      clearDraft(draftKey);
      return;
    }

    saveDraft(draftKey, form);
  }, [form, id, draftKey, draftPromptOpen]);

  const handleUseDraft = () => {
    if (!draftToRestore) return;
    setForm((prev) => ({
      ...prev,
      ...draftToRestore,
      assignedUserId:
        draftToRestore.assignedUserId || prev.assignedUserId || user?.id,
      assignedUserName:
        draftToRestore.assignedUserName ||
        prev.assignedUserName ||
        defaultUserName,
      taskParentId: draftToRestore.taskParentId || taskParentId,
    }));
    setDraftPromptOpen(false);
  };

  const handleStartNew = () => {
    clearDraft(draftKey);
    localStorage.removeItem("pendingAttachments");
    localStorage.removeItem("pendingNoteAttachments");
    initNewForm();
    setDraftPromptOpen(false);
  };

  const handleTextChange =
    (field: keyof Partial<Task>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSelectChange =
    (field: keyof Partial<Task>) =>
    (e: SelectChangeEvent<TaskStatusKey | string>) => {
      const newValue = e.target.value as TaskStatusKey | string;

      if (field === "assignedUserId") {
        const selectedUser = users.find((u) => u.id === newValue);
        setForm((prev) => ({
          ...prev,
          assignedUserId: newValue,
          assignedUserName: selectedUser?.name || defaultUserName,
        }));
        return;
      }

      setForm((prev) => ({
        ...prev,
        [field]: newValue,
        ...(field === "status" && newValue !== "Completed"
          ? { resolution: undefined, dateCompleted: undefined }
          : field === "status" && newValue === "Completed"
          ? {
              dateCompleted: dayjs()
                .tz(DEVICE_TZ)
                .utc()
                .format("YYYY-MM-DDTHH:mm:ss[Z]"),
            }
          : {}),
      }));
    };

  const handleDateChange =
    (field: keyof Partial<Task>) => (newValue: Dayjs | null) => {
      setForm((prev) => ({
        ...prev,
        [field]: toServerUtcIso(newValue),
      }));
    };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      if (!form.name) {
        setError(t("tasks.detail.requiredTitle"));
        return;
      }
      if (!form.assignedUserId) {
        setError(t("tasks.detail.requiredAssignee"));
        return;
      }

      const startRaw = form.dateStart || "";
      const endRaw = form.dateEnd || "";

      if (startRaw && !dayjs(startRaw).isValid()) {
        setError(t("tasks.detail.invalidStartDate"));
        return;
      }
      if (endRaw && !dayjs(endRaw).isValid()) {
        setError(t("tasks.detail.invalidEndDate"));
        return;
      }

      const startUtcIso = startRaw
        ? normalizeIsoUtc(startRaw)
        : dayjs().tz(DEVICE_TZ).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");

      const endUtcIso = endRaw
        ? normalizeIsoUtc(endRaw)
        : dayjs
            .utc(startUtcIso)
            .add(DEFAULT_CONFIG.TASK_END_DATE_OFFSET_HOURS, "hour")
            .format("YYYY-MM-DDTHH:mm:ss[Z]");

      if (dayjs.utc(endUtcIso).isBefore(dayjs.utc(startUtcIso))) {
        setError(t("tasks.detail.endDateBeforeStart"));
        return;
      }

      if (form.status === "Completed" && !form.resolution) {
        setError(t("tasks.detail.requiredResolution"));
        return;
      }

      const formattedForm: Partial<Task> = {
        ...form,
        dateStart: startUtcIso,
        dateEnd: endUtcIso,
        assignedUserId: form.assignedUserId,
        cSolution: form.resolution,
        dateCompleted:
          form.status === "Completed"
            ? normalizeIsoUtc(form.dateCompleted) ||
              dayjs().tz(DEVICE_TZ).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
            : undefined,
      };

      let taskId = id;

      if (id === "new") {
        formattedForm.attachments = attachments
          .filter((a) => a.file)
          .map((a) => a.file!);

        const newTask = await createTask(formattedForm);
        taskId = newTask.id;

        for (const subtask of pendingSubtasks) {
          const subtaskData: Partial<Subtask> = {
            ...subtask,
            taskParentId: taskId,
            status: subtask.status as TaskStatusKey,
          };
          await createTask(subtaskData);
        }

        for (const note of notes) {
          const noteData = {
            parentId: taskId!,
            parentType: "Task",
            post: note.text || note,
            type: "Post",
            isInternal: false,
            ...(note.attachmentsIds && {
              attachmentsIds: note.attachmentsIds,
              attachmentsNames: note.attachmentsNames,
              attachmentsTypes: note.attachmentsTypes,
            }),
          };
          await createNote(noteData);
        }

        setPendingSubtasks([]);
        setAttachments([]);
        localStorage.removeItem("pendingAttachments");
        localStorage.removeItem("pendingNoteAttachments");
        clearDraft(draftKey);
        setIsEditing(false);
        navigate(taskParentId ? `/task/${taskParentId}` : `/task/${taskId}`);
        setSuccess(
          taskParentId
            ? t("tasks.detail.createSubtaskSuccess")
            : t("tasks.detail.createSuccess"),
        );
      } else {
        await updateTask(taskId!, formattedForm);
        setIsEditing(false);
        setAnchorEl(null);
        setSuccess(t("tasks.detail.updateSuccess"));
      }

      await loadAll();
    } catch (error: any) {
      console.error("Ошибка при сохранении задачи:", error);
      const message =
        error.response?.data?.messageTranslation?.label === "validationFailure"
          ? t("tasks.detail.validationError", {
              field: error.response?.data?.messageTranslation?.data?.field,
            })
          : error.response?.data?.messageTranslation?.label ===
            "cannotRelateNonExisting"
          ? t("tasks.detail.nonExistingUser", { userId: form.assignedUserId })
          : t("tasks.detail.saveError");
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || id === "new") return;
    setIsLoading(true);
    try {
      await deleteTask(id);
      navigate(taskParentId ? `/task/${taskParentId}` : "/tasks");
      setSuccess(t("tasks.detail.deleteSuccess"));
    } catch {
      setError(t("tasks.detail.deleteError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (id === "new") {
      attachments.forEach(({ attachment }) => {
        if (attachment.url.startsWith("blob:")) {
          URL.revokeObjectURL(attachment.url);
        }
      });
      localStorage.removeItem("pendingAttachments");
      localStorage.removeItem("pendingNoteAttachments");
      navigate(taskParentId ? `/task/${taskParentId}` : "/tasks");
    } else {
      setIsEditing(false);
      loadAll();
    }
  };

  const handleRefresh = async () => {
    if (id === "new") {
      setNotes([]);
      const pendingAttachments: Attachment[] = JSON.parse(
        localStorage.getItem("pendingAttachments") || "[]"
      );
      setAttachments(
        pendingAttachments.map((a: Attachment) => ({ attachment: a }))
      );
    } else {
      await loadAll();
    }
  };

  const setNotification = (type: "success" | "error", message: string) => {
    if (type === "success") {
      setSuccess(message);
      setError(null);
    } else {
      setError(message);
      setSuccess(null);
    }
  };

  const mappedSubtasks: ItemWithSource[] = subtasks
    .map((tsk) => ({
      id: tsk.id,
      name: tsk.name,
      assignedUserName: tsk.assignedUserName || defaultUserName,
      status: tsk.status as TaskStatusKey,
      dateStart: tsk.dateStart || "",
      dateEnd: tsk.dateEnd || "",
      description: tsk.description || "",
      source: "task" as const,
      parentId: tsk.taskParentId,
      resolution: tsk.resolution,
    }))
    .concat(
      pendingSubtasks.map((tsk, index) => ({
        id: tsk.id || `${index}`,
        name: tsk.name || "",
        assignedUserName: tsk.assignedUserName || defaultUserName,
        status: (tsk.status || "Not Started") as TaskStatusKey,
        dateStart: tsk.dateStart || "",
        dateEnd: tsk.dateEnd || "",
        description: tsk.description || "",
        source: "task" as const,
        parentId: tsk.taskParentId || id,
        resolution: tsk.resolution,
      }))
    );

  const getStatusConfig = (status: StatusKey, source: "task" | "meeting") => {
    if (source === "task") {
      const key = status as TaskStatusKey;
      return {
        color: taskStatusColors[key]?.color || "#000000",
        translation: statusTranslations[key] || status,
      };
    } else {
      return {
        color: "#000000",
        translation: status,
      };
    }
  };

  return {
    task,
    isEditing,
    setIsEditing,
    form,
    users,
    attachments,
    notes,
    mappedSubtasks,
    anchorEl,
    setAnchorEl,
    handleTextChange,
    handleSelectChange,
    handleDateChange,
    handleSave,
    handleDelete,
    handleCancel,
    handleOpenMenu: (event: React.MouseEvent<HTMLElement>) =>
      setAnchorEl(event.currentTarget),
    handleCloseMenu: () => setAnchorEl(null),
    handleRefresh,
    getStatusConfig,
    id,
    error,
    setError,
    success,
    setSuccess,
    isLoading,
    setNotification,
    draftPromptOpen,
    handleUseDraft,
    handleStartNew,
  };
};
